import { lazyWithRetry as lazy } from "./lazyWithRetry";

/** Enums */
import { securityKeys } from "../enums/SecurityKeys.ENUM";
import { productModuleCodes } from "../enums/ProductModuleCodes.ENUM";
import { isDevApiErrorLoggingEnabled } from "../utils/dev/devApiErrorStore";

/** Components */
import JobRequisitionNew from "../pages/jobRequisition/JobRequisitionNew.jsx";
import FollowUpApplicant from "../pages/followUpApplicant/FollowUpApplicant.jsx";
import ScheduleInterview from "../pages/scheduleInterview/ScheduleInterview.jsx";
import AddManpowerRequisition from "../pages/jobRequisition/AddManpowerRequisition.jsx";

/** Common Page Imports */
const NotFound = lazy(() => import("../pages/notFound/NotFound"));
const UnderConstruction = lazy(() => import("../pages/underConstruction/UnderConstruction"));

/** Page Imports */
const Dashboard = lazy(() => import("../pages/dashboard/Dashboard"));
const NotificationCenter = lazy(() => import("../pages/notifications/NotificationCenter"));
const Profile = lazy(() => import("../pages/profile/Profile"));

const Roles = lazy(() => import("../pages/roles/Roles"));
const InterviewType = lazy(() => import("../pages/interviewType/InterviewType"));
const RejectedReason = lazy(() => import("../pages/rejectedReason/RejectedReason"))
const Department = lazy(() => import("../pages/department/Department"));
const InterviewRound = lazy(() => import("../pages/interviewRound/InterviewRound"));
const ApplicantStatus = lazy(() => import("../pages/applicantStatus/ApplicantStatus"))
const SourceManagement = lazy(() => import("../pages/sourceManagement/SourceManagement"));
const ChannelManagement = lazy(() => import("../pages/channelManagement/ChannelManagement"));
const UserManagement = lazy(() => import("../pages/userManagement/Users"))
const AddUser = lazy(() => import("../pages/userManagement/AddUser"))
const JobOpening = lazy(() => import("../pages/jobOpening/JobOpening"))
const EmailTemplate = lazy(() => import("../pages/emailTemplate/EmailTemplate"))
const AddEmailTemplate = lazy(() => import("../pages/emailTemplate/AddEmailTemplate"))
const SkillCategory = lazy(() => import("../pages/skillCategory/SkillCategory"))
const Applicants = lazy(() => import("../pages/applicants/Applicants"))
const ApplicantDetails = lazy(() => import("../pages/applicants/components/ApplicantDetails"))
const CandidateAssessmentReview = lazy(() => import("../pages/candidateAssessment/CandidateAssessmentReview"))
const DeletedApplicants = lazy(() => import("../pages/deletedApplicants/DeletedApplicants"))
const EmploymentTypes = lazy(() => import("../pages/employmentType/EmploymentType"))
const FollowUpReason = lazy(() => import("../pages/followUpReason/FollowUpReason"))
const Security = lazy(() => import("../pages/security/Security.jsx"));
const SkillManagement = lazy(() => import("../pages/skillmanagement/SkillManagement"));
const CapabilityFramework = lazy(() => import("../pages/capabilityFramework/CapabilityFramework"));
const CapabilityGraphEdges = lazy(() => import("../pages/skillmanagement/CapabilityGraphEdges"));
const InterviewWorkflowStage = lazy(() => import("../pages/interviewWorkflowStage/InterviewWorkflowStage"));
const WorkflowDetails = lazy(() => import("../pages/interviewWorkflowStage/components/WorkflowDetails"));
const ApprovalConfiguration = lazy(() => import("../pages/approvalConfiguration/ApprovalConfiguration"));
const ApprovalWorkflow = lazy(() => import("../pages/approvalWorkflow/ApprovalWorkflow"));
const QuestionBank = lazy(() => import("../pages/questionBank/QuestionBank"));
const AddEditQuestionBank = lazy(() => import("../pages/questionBank/AddEditQuestionBank"));
// Test Builder page
const TestTemplate = lazy(() => import("../pages/testTemplate/TestTemplate"));
const AddEditTestTemplate = lazy(() => import("../pages/testTemplate/AddEditTestTemplate")); 
const InterviewerCalendar = lazy(() => import("../pages/interviewerCalendar/InterviewerCalendar"));
const DynamicForms = lazy(() => import("../pages/dynamicForms/DynamicForms"));
const FormBuilder = lazy(() => import("../pages/dynamicForms/FormBuilder"));
const FormAssociations = lazy(() => import("../pages/formAssociations/FormAssociations"));

const ScheduledInterviews = lazy(() => import("../pages/interview/ScheduledInterview"));
const CompletedInterviews = lazy(() => import("../pages/interview/CompletedInterview"));
const ReportAccess = lazy(() => import("../pages/Reports/ReportAccess"));
const Organizations = lazy(() => import("../pages/organization/Organizations"));
const OrganizationDetails = lazy(() => import("../pages/organization/OrganizationDetails"));

const ManageReport = lazy(() => import("../pages/manageReport/ManageReport"));
const BatchProcess = lazy(() => import("../pages/batch-process/BatchProcess"));
const BatchResumes = lazy(() => import("../pages/batch-process/BatchResumes"));

const AllParsedResumes = lazy(() => import("../pages/batch-process/components/AllParsedResumes"));
const ModifyApplicantDetails = lazy(() => import("../pages/batch-process/components/ModifyApplicantDetails"));

const AddJobTemplate = lazy(() => import("../pages/jobTemplate/AddJobTemplatePage"));
const JobTemplate = lazy(() => import("../pages/jobTemplate/JobTemplate"));
const EditJobTemplate = lazy(() => import("../pages/jobTemplate/AddJobTemplatePage"));
const AIControlCenter = lazy(() => import("../pages/aiControlCenter/AIControlCenter"));
const AIProvidersPage = lazy(() => import("../pages/aiPlatform/AIProvidersPage"));
const AIFeaturesPage = lazy(() => import("../pages/aiPlatform/AIFeaturesPage"));
const AIFeatureConfigPage = lazy(() => import("../pages/aiPlatform/AIFeatureConfigPage"));
const AIPromptLibraryPage = lazy(() => import("../pages/aiPlatform/AIPromptLibraryPage"));
const AIAgentsPage = lazy(() => import("../pages/aiPlatform/AIAgentsPage"));
const AIAgentCapabilitiesPage = lazy(() => import("../pages/aiPlatform/AIAgentCapabilitiesPage"));
const AIAgentInboxPage = lazy(() => import("../pages/aiPlatform/AIAgentInboxPage"));
const AIAgentRunsPage = lazy(() => import("../pages/aiPlatform/AIAgentRunsPage"));
const AIAgentKpiPage = lazy(() => import("../pages/aiPlatform/AIAgentKpiPage"));
const AIExecutionLogsPage = lazy(() => import("../pages/aiPlatform/AIExecutionLogsPage"));
const AIDevApiErrorLogsPage = lazy(() => import("../pages/systemHealth/AIDevApiErrorLogsPage"));
const SystemHealthSettingsPage = lazy(() => import("../pages/systemHealth/SystemHealthSettingsPage"));
const SystemServerErrorsPage = lazy(() => import("../pages/systemHealth/SystemServerErrorsPage"));
const SystemHealthDashboardPage = lazy(() => import("../pages/systemHealth/SystemHealthDashboardPage"));
const RecruitmentAiAssistantPage = lazy(() => import("../pages/recruitmentAiAssistant/RecruitmentAiAssistantPage"));
const OfferManagement = lazy(() => import("../pages/offers/OfferManagement"));
const RecruitmentCommandCenter = lazy(() => import("../pages/recruitmentCommandCenter/RecruitmentCommandCenter"));
const RecruitmentSlaPolicy = lazy(() => import("../pages/recruitmentSlaPolicy/RecruitmentSlaPolicy"));
const TalentCrm = lazy(() => import("../pages/talentCrm/TalentCrm"));
const TalentCrmDetail = lazy(() => import("../pages/talentCrm/TalentCrmDetail"));
const TalentTags = lazy(() => import("../pages/talentCrm/TalentTags"));
const TalentCrmMetrics = lazy(() => import("../pages/talentCrm/TalentCrmMetrics"));
const TalentPools = lazy(() => import("../pages/talentPools/TalentPools"));
const TalentRediscovery = lazy(() => import("../pages/talentRediscovery/TalentRediscovery"));
const KnowledgeRag = lazy(() => import("../pages/knowledgeRag/KnowledgeRag"));
const QualityOfHire = lazy(() => import("../pages/qualityOfHire/QualityOfHire"));

export const ComponentNavigation = [
  {
    path: "pageNotFound",
    exact: true,
    title: "Page Not Found",
    component: NotFound,
    securityKey: "",
  },
  {
    path: "/",
    exact: true,
    title: "Dashboard",
    component: Dashboard,
    securityKey: securityKeys.DASHBOARD,
  },
  {
    path: "/recruitment-command-center",
    exact: true,
    title: "Recruitment Command Center",
    component: RecruitmentCommandCenter,
    securityKey: securityKeys.RECRUITMENT_COMMAND_CENTER,
  },
  {
    path: "/recruitment-sla-policy",
    exact: true,
    title: "Recruitment SLA Policy",
    component: RecruitmentSlaPolicy,
    securityKey: securityKeys.RECRUITMENT_SLA_POLICY,
  },
  {
    path: "/talent-crm",
    exact: true,
    title: "Talent CRM",
    component: TalentCrm,
    securityKey: securityKeys.TALENT_CRM,
    moduleCode: productModuleCodes.TALENT_CRM,
  },
  {
    path: "/talent-crm/:id",
    exact: true,
    title: "Talent CRM Detail",
    component: TalentCrmDetail,
    securityKey: securityKeys.LIST_TALENT_CRM,
    moduleCode: productModuleCodes.TALENT_CRM,
  },
  {
    path: "/talent-pools",
    exact: true,
    title: "Talent Pools",
    component: TalentPools,
    securityKey: securityKeys.TALENT_POOLS,
    moduleCode: productModuleCodes.TALENT_CRM,
  },
  {
    path: "/talent-tags",
    exact: true,
    title: "Talent Tags",
    component: TalentTags,
    securityKey: securityKeys.MANAGE_TALENT_TAGS,
    moduleCode: productModuleCodes.TALENT_CRM,
  },
  {
    path: "/talent-crm-metrics",
    exact: true,
    title: "Talent CRM Metrics",
    component: TalentCrmMetrics,
    securityKey: securityKeys.LIST_TALENT_CRM,
    moduleCode: productModuleCodes.TALENT_CRM,
  },
  {
    path: "/talent-rediscovery",
    exact: true,
    title: "Talent Rediscovery",
    component: TalentRediscovery,
    securityKey: securityKeys.TALENT_REDISCOVERY,
    moduleCode: productModuleCodes.TALENT_REDISCOVERY,
  },
  {
    path: "/knowledge-intelligence",
    exact: true,
    title: "Knowledge Intelligence",
    component: KnowledgeRag,
    securityKey: securityKeys.KNOWLEDGE_INTELLIGENCE,
    moduleCode: productModuleCodes.KNOWLEDGE_RAG,
  },
  {
    path: "/quality-of-hire",
    exact: true,
    title: "Quality Of Hire",
    component: QualityOfHire,
    securityKey: securityKeys.QUALITY_OF_HIRE,
    moduleCode: productModuleCodes.QUALITY_OF_HIRE,
  },
  {
    path: "/notifications",
    exact: true,
    title: "Notifications",
    component: NotificationCenter,
    securityKey: "",
  },
  {
    path: "/profile/:id",
    exact: true,
    title: "Profile",
    component: Profile,
    securityKey: "",
  },

/******************* System Configuration *******************/
  {
    path: "/interview-round",
    exact: true,
    title: "Interview Round",
    component: InterviewRound,
    securityKey: securityKeys.INTERVIEW_ROUND,
  },
  {
    path: "/interview-type",
    exact: true,
    title: "Interview Type",
    component: InterviewType,
    securityKey: securityKeys.INTERVIEW_TYPE,
  },
  {
    path: "/job-opening",
    exact: true,
    title: "Job Position",
    component: JobOpening,
    securityKey: securityKeys.JOB_OPENINGS,
  },
  {
    path: "/rejected-reason",
    exact: true,
    title: "Rejected Reason",
    component: RejectedReason,
    securityKey: securityKeys.REJECTED_REASON,
  },
  {
    path: "/followup-reason",
    exact: true,
    title: "Followup Reason",
    component: FollowUpReason,
    securityKey: securityKeys.FOLLOWUP_REASON,
  },
  {
    path: "/approval-configuration/:id",
    exact: true,
    title: "Approval Configuration",
    component: ApprovalConfiguration,
    securityKey: securityKeys.APPROVAL_CONFIGURATION,
  },
  {
    path: "/approval-workflow",
    exact: true,
    title: "Approval Workflow",
    component: ApprovalWorkflow,
    securityKey: securityKeys.APPROVAL_CONFIGURATION,
  },



  /******************* Management *******************/
  {
    path: "/users",
    exact: true,
    title: "Manage Users",
    component: UserManagement,
    securityKey: securityKeys.MANAGE_USERS,
  },
  {
    path: "/add-user",
    exact: true,
    title: "Add User",
    component: AddUser,
    securityKey: securityKeys.ADD_USERS,
  },
  {
    path: "/add-user/:id",
    exact: true,
    title: "Update User",
    component: AddUser,
    securityKey: securityKeys.UPDATE_USERS,
  },
  {
    path: "/roles",
    exact: true,
    title: "Manage Roles",
    component: Roles,
    securityKey: securityKeys.MANAGE_ROLES,
  },
  {
    path: "/email-template",
    exact: true,
    title: "Email Template",
    component: EmailTemplate,
    securityKey: securityKeys.MANAGE_EMAIL_TEMPLATE,
  },
  {
    path: "/add-email-template",
    exact: true,
    title: "Add Email Template",
    component: AddEmailTemplate,
    securityKey: securityKeys.ADD_EMAIL_TEMPLATE,
  },
  {
    path: "/add-email-template/:id",
    exact: true,
    title: "Update Email Template",
    component: AddEmailTemplate,
    securityKey: securityKeys.UPDATE_EMAIL_TEMPLATE,
  },
  {
    path: "/system-actions",
    exact: true,
    title: "System Actions",
    component: UnderConstruction,
    securityKey: securityKeys.SYSTEM_ACTIONS,
  },
  {
    path: "/ai-control-center",
    exact: true,
    title: "AI Control Center",
    component: AIControlCenter,
    securityKey: securityKeys.AI_CONTROL_CENTER,
    moduleCode: productModuleCodes.AI_PLATFORM,
  },
  {
    path: "/ai-providers",
    exact: true,
    title: "AI Providers",
    component: AIProvidersPage,
    securityKey: securityKeys.AI_CONTROL_CENTER,
    moduleCode: productModuleCodes.AI_PLATFORM,
  },
  {
    path: "/ai-features",
    exact: true,
    title: "AI Features",
    component: AIFeaturesPage,
    securityKey: securityKeys.AI_CONTROL_CENTER,
    moduleCode: productModuleCodes.AI_PLATFORM,
  },
  {
    path: "/ai-feature-config",
    exact: true,
    title: "AI Feature Configuration",
    component: AIFeatureConfigPage,
    securityKey: securityKeys.AI_CONTROL_CENTER,
    moduleCode: productModuleCodes.AI_PLATFORM,
  },
  {
    path: "/ai-prompt-library",
    exact: true,
    title: "Prompt Library",
    component: AIPromptLibraryPage,
    securityKey: securityKeys.AI_CONTROL_CENTER,
    moduleCode: productModuleCodes.AI_PLATFORM,
  },
  {
    path: "/ai-agents",
    exact: true,
    title: "AI Agents",
    component: AIAgentsPage,
    securityKey: [securityKeys.AGENT_OPS, securityKeys.AI_CONTROL_CENTER],
    moduleCode: productModuleCodes.AI_AGENTS,
  },
  {
    path: "/ai-agent-capabilities",
    exact: true,
    title: "Agent Capabilities",
    component: AIAgentCapabilitiesPage,
    securityKey: [securityKeys.AGENT_OPS, securityKeys.AI_CONTROL_CENTER],
    moduleCode: productModuleCodes.AI_AGENTS,
  },
  {
    path: "/ai-agent-inbox",
    exact: true,
    title: "Agent Inbox",
    component: AIAgentInboxPage,
    securityKey: [securityKeys.AGENT_OPS, securityKeys.AI_CONTROL_CENTER],
    moduleCode: productModuleCodes.AI_AGENTS,
  },
  {
    path: "/ai-agent-runs",
    exact: true,
    title: "Agent Run History",
    component: AIAgentRunsPage,
    securityKey: [securityKeys.AGENT_OPS, securityKeys.AI_CONTROL_CENTER],
    moduleCode: productModuleCodes.AI_AGENTS,
  },
  {
    path: "/ai-agent-kpi",
    exact: true,
    title: "Agent KPI",
    component: AIAgentKpiPage,
    securityKey: [securityKeys.AGENT_OPS, securityKeys.AI_CONTROL_CENTER],
    moduleCode: productModuleCodes.AI_AGENTS,
  },
  {
    path: "/ai-execution-logs",
    exact: true,
    title: "AI Execution Logs",
    component: AIExecutionLogsPage,
    securityKey: securityKeys.AI_CONTROL_CENTER,
    moduleCode: productModuleCodes.AI_PLATFORM,
  },
  ...(isDevApiErrorLoggingEnabled()
    ? [
        {
          path: "/system-api-errors",
          exact: true,
          title: "API Errors (Dev)",
          component: AIDevApiErrorLogsPage,
          securityKey: securityKeys.SYSTEM_ACTIONS,
        },
        {
          path: "/ai-dev-api-errors",
          exact: true,
          title: "API Errors (Dev)",
          component: AIDevApiErrorLogsPage,
          securityKey: securityKeys.SYSTEM_ACTIONS,
        },
      ]
    : []),
  {
    path: "/system-health",
    exact: true,
    title: "System Health",
    component: SystemHealthDashboardPage,
    securityKey: securityKeys.SYSTEM_ACTIONS,
  },
  {
    path: "/system-server-errors",
    exact: true,
    title: "Server Exceptions",
    component: SystemServerErrorsPage,
    securityKey: securityKeys.SYSTEM_ACTIONS,
  },
  {
    path: "/system-health-settings",
    exact: true,
    title: "Health Settings",
    component: SystemHealthSettingsPage,
    securityKey: securityKeys.SYSTEM_ACTIONS,
  },
  {
    path: "/recruitment-ai-assistant",
    exact: true,
    title: "MolAssist",
    component: RecruitmentAiAssistantPage,
    securityKey: securityKeys.RECRUITMENT_AI_ASSISTANT,
    moduleCode: productModuleCodes.MOLASSIST,
  },
  {
    path: "/applicant-status",
    exact: true,
    title: "Applicant Status",
    component: ApplicantStatus,
    securityKey: securityKeys.MANAGE_APPLICANT_STATUS,
  },
  {
    path: "/channel",
    exact: true,
    title: "Manage Channel",
    component: ChannelManagement,
    securityKey: securityKeys.MANAGE_CHANNEL,
  },
  {
    path: "/source",
    exact: true,
    title: "Manage Source",
    component: SourceManagement,
    securityKey: securityKeys.MANAGE_SOURCE,
  },
  {
    path: "/department",
    exact: true,
    title: "Department",
    component: Department,
    securityKey: securityKeys.MANAGE_DEPARTMENTS,
  },
  {
    path: "/manage-reports",
    exact: true,
    title: "Manage Reports",
    component: ManageReport,
    securityKey: securityKeys.MANAGE_REPORTS,
  },
  {
    path: "/batch-process",
    exact: true,
    title: "Batch Process",
    component: BatchProcess,
    securityKey: securityKeys.BATCH_RESUME_PROCESS,
  },
  // {
  //   path: "/batch-process-list",
  //   exact: true,
  //   title: "Batch Process List",
  //   component: BatchProcess,
  //   securityKey: securityKeys.DASHBOARD,
  // },
  {
    path: "/batch-resumes/:id",
    exact: true,
    title: "Batch Resumes",
    component: BatchResumes,
    securityKey: securityKeys.BATCH_RESUME_PROCESS,
  },
  {
    path: "/all-parsed-resumes",
    exact: true,
    title: "All Parsed Resumes",
    component: AllParsedResumes,
    securityKey: securityKeys.PARSED_RESUMES,
  },
  {
    // path: "/modify-applicant-details/:resumeId/:id",
    path: "/modify-applicant-details/:id",
    exact: true,
    title: "Modify Applicant Details",
    component: ModifyApplicantDetails,
    securityKey: securityKeys.UPDATE_APPLICANT, //to be changed
    isFullScreen: true,
  },


  /******************* Skill Configuration *******************/
  {
    path: "/skills",
    exact: true,
    title: "Skills",
    component: SkillManagement,
    securityKey: securityKeys.SKILL_MANAGEMENT,
  },

  {
    path: "/skill-category",
    exact: true,
    title: "Skill Category",
    component: SkillCategory,
    securityKey: securityKeys.SKILL_CATEGORIES,
  },
  {
    path: "/capability-framework",
    exact: true,
    title: "Capability Framework",
    component: CapabilityFramework,
    securityKey: securityKeys.SKILL_MANAGEMENT,
  },
  {
    path: "/capability-graph-edges",
    exact: true,
    title: "Capability Graph Edges",
    component: CapabilityGraphEdges,
    securityKey: securityKeys.SKILL_MANAGEMENT,
  },
  {
    path: "/questions",
    exact: true,
    title: "Question Set",
    component: QuestionBank,
    securityKey: securityKeys.QUESTION_BANK,
  },
  {
    path: "/questions/add",
    exact: true,
    title: "Add Question Set",
    component: AddEditQuestionBank,
    securityKey: securityKeys.ADD_QUESTION_BANK,
  },
  {
    path: "/questions/edit/:id",
    exact: true,
    title: "Edit Question Set",
    component: AddEditQuestionBank,
    securityKey: securityKeys.UPDATE_QUESTION_BANK,
  },
  {
    path: "/test-templates",
    exact: true,
    title: "Test Builder",
    component: TestTemplate,
    securityKey: securityKeys.TEST_TEMPLATE,
  },
  {
    path: "/test-templates/edit/:id",
    exact: true,
    title: "Edit Test",
    component: AddEditTestTemplate,
    securityKey: securityKeys.UPDATE_TEST_TEMPLATE,
  },
  {
    path: "/dynamic-forms",
    exact: true,
    title: "Dynamic Forms",
    component: DynamicForms,
    securityKey: securityKeys.DYNAMIC_FORMS,
  },
  {
    path: "/dynamic-forms/edit/:id",
    exact: true,
    title: "Edit Dynamic Form",
    component: FormBuilder,
    securityKey: securityKeys.UPDATE_DYNAMIC_FORMS,
  },
  {
    path: "/form-associations",
    exact: true,
    title: "Form Associations",
    component: FormAssociations,
    securityKey: securityKeys.FORM_ASSOCIATIONS,
  },
  {
    path: "/admin/form-associations",
    exact: true,
    title: "Form Associations",
    component: FormAssociations,
    securityKey: securityKeys.FORM_ASSOCIATIONS,
  },
  {
    path: "/applicants",
    exact: true,
    title: "Candidates",
    component: Applicants,
    securityKey: securityKeys.LIST_APPLICANT,
  },
  {
    path: "/offers",
    exact: true,
    title: "Offers",
    component: OfferManagement,
    securityKey: securityKeys.LIST_OFFERS,
  },

  {
    path: "/applicants/follow-up",
    exact: true,
    title: "Follow Up Candidate",
    component: FollowUpApplicant,
    securityKey: securityKeys.APPLICANT,
  },
  {
    path: "/applicants/schedule",
    exact: true,
    title: "Schedule Interview",
    component: ScheduleInterview,
    securityKey: securityKeys.SCHEDULED_INTERVIEWS,
  },
  {
    path: "/deleted-applicants",
    exact: true,
    title: "Deleted Applicants",
    component: DeletedApplicants,
    securityKey: securityKeys.RESTORE_APPLICANT,
  },
  // {
  //   path: "/job-requisition",
  //   exact: true,
  //   title: "JobRequisition",
  //   component: JobRequisition,
  //   securityKey: securityKeys.JOB_REQUISITION,
  // },
  {
    path: "/job-requisition",
    exact: true,
    title: "Job Requisition",
    component: JobRequisitionNew,
    securityKey: securityKeys.JOB_REQUISITION,
  },
  {
    path: "/add-requisition",
    exact: true,
    title: "Add Requisition",
    // component: AddJobRequisition,
    component: AddManpowerRequisition,
    securityKey: securityKeys.JOB_REQUISITION, //to be changed
  },
  {
    path: "/update-job-requisition/:id",
    exact: true,
    title: "Update Requisition",
    // component: AddJobRequisition,
    component: AddManpowerRequisition,
    securityKey: securityKeys.JOB_REQUISITION,//to be changed
  },
  {
    path: "/applicant-details/:id",
    exact: true,
    title: "Candidate Details",
    component: ApplicantDetails,
    securityKey: securityKeys.APPLICANT
  },
  {
    path: "/candidate-assessment/review/:assignmentId",
    exact: true,
    title: "Assessment Review",
    component: CandidateAssessmentReview,
    securityKey: securityKeys.APPLICANT,
  },
  {
    path: "/my-interview/scheduled",
    exact: true,
    title: "Scheduled Interviews",
    component: ScheduledInterviews,
    securityKey: securityKeys.SCHEDULED_INTERVIEWS
  },
  {
    path: "/interview/completed",
    exact: true,
    title: "Completed Interviews",
    component: CompletedInterviews,
    securityKey: securityKeys.COMPLETED_INTERVIEWS
  },
  {
    path: "/interview-workflow",
    exact: true,
    title: "Interview Workflow",
    component: InterviewWorkflowStage,
    securityKey: securityKeys.LIST_INTERVIEW_WORKFLOW,
  },
  {
    path: "/interview-workflow/:id",
    exact: true,
    title: "Workflow Details",
    component: WorkflowDetails,
    securityKey: securityKeys.INTERVIEW_WORKFLOW,
  },
  {
    path: "/employment-types",
    exact: true,
    title: "Employment Types",
    component: EmploymentTypes,
    securityKey: securityKeys.EMPLOYMENT_TYPES,
  },
  // {
  //   path: "/followup-reason"
  //   exact: true,
  //   title: "Followup Reasons",
  //   component: FollowUpReason,
  //   securityKey: securityKeys.FOLLOWUP_REASON,
  // },
  /******************* Security *******************/
  {
    path: "/security",
    exact: true,
    title: "Security",
    component: Security,
    securityKey: securityKeys.SECURITY,
  },

  /******************* Organizations *******************/
  {
    path: "/organizations",
    exact: true,
    title: "Organizations",
    component: Organizations,
    securityKey: securityKeys.ORGANIZATION,
  },
  {
    path: "/organization-details/:id",
    exact: true,
    title: "Organization Deails",
    component: OrganizationDetails,
    securityKey: securityKeys.ORGANIZATION,
  },

  /******************* Reports *******************/
  {
    path: "/candidate-report",
    exact: true,
    title: "Candidate Report",
    component: ReportAccess,
  },
  {
    path: "/job-report",
    exact: true,
    title: "Job Report",
    component: ReportAccess,
  },
  {
    path: "/activity-log-report",
    exact: true,
    title: "Activity Log Report",
    component: ReportAccess,
  },
  {
    path: "/talent-crm-report",
    exact: true,
    title: "Talent CRM Report",
    component: ReportAccess,
  },
  {
    path: "/my-interview/calendar",
    exact: true,
    title: "My Interview Calendar",
    component: InterviewerCalendar,
    securityKey: securityKeys.MY_INTERVIEW_CALENDAR,
  },
  {
    path: "/job-description-templates",
    exact: true,
    title: "Job Success Profiles",
    component: JobTemplate,
    securityKey: securityKeys.JOB_TEMPLATES,
  },
  {
    path: "/add-job-template",
    exact: true,
    title: "Add Job Success Profile",
    component: AddJobTemplate,
    securityKey: securityKeys.ADD_JOB_TEMPLATES,
  },
  {
    path: "/edit-job-template/:id",
    exact: true,
    title: "Edit Job Success Profile",
    component: EditJobTemplate,
    securityKey: securityKeys.UPDATE_JOB_TEMPLATES,
  }
];
